// @ts-nocheck
import React, { useCallback, useEffect, useState } from "react";
import { apiFetchInit } from "@/legacy/helpersModule";
import { Modal } from "@/shared/components/ui/Modal";
import { Button, Skeleton, useToast } from "@/shared/components/ui";

const PAGE_SIZE = 50;

async function downloadRunsExportCsv() {
  const r = await fetch("/api/admin/cv-analyser/runs/export", apiFetchInit());
  if (!r.ok) {
    const err = await r.json().catch(() => ({}));
    throw new Error(err.error || "export");
  }
  const blob = await r.blob();
  const url = URL.createObjectURL(blob);
  const stamp = new Date().toISOString().slice(0, 10);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cv-analyser-audit-${stamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function splitDateTime(iso) {
  if (!iso) return { date: "—", time: "—" };
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: "—", time: "—" };
    const date = d.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
    const time = d.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZone: "Asia/Kolkata",
    });
    return { date, time };
  } catch {
    return { date: "—", time: "—" };
  }
}

export function CvAnalyserLogsModal({ onClose }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [analysedCount, setAnalysedCount] = useState(0);
  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);

  const load = useCallback(async ({ append = false, offset = 0 } = {}) => {
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setError("");
    }
    try {
      const [statsRes, runsRes] = await Promise.all([
        append
          ? Promise.resolve(null)
          : fetch("/api/admin/cv-analyser/runs/stats", apiFetchInit()),
        fetch(
          `/api/admin/cv-analyser/runs?limit=${PAGE_SIZE}&offset=${offset}`,
          apiFetchInit(),
        ),
      ]);

      if (!append && statsRes) {
        const stats = await statsRes.json().catch(() => ({}));
        if (!statsRes.ok) {
          throw new Error(stats.error || "Failed to load analysed count");
        }
        setAnalysedCount(Number(stats.analysedCount) || 0);
      }

      const data = await runsRes.json().catch(() => ({}));
      if (!runsRes.ok) {
        throw new Error(data.error || "Failed to load logs");
      }
      const next = Array.isArray(data.runs) ? data.runs : [];
      setTotal(Number(data.total) || 0);
      setRuns((prev) => (append ? [...prev, ...next] : next));
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    load({ append: false, offset: 0 });
  }, [load]);

  return (
    <Modal
      title="CV Analyser logs"
      onClose={onClose}
      wide
      panelClassName="!max-w-5xl"
    >
      {error ? (
        <p className="text-sm text-red-600 mb-4">{error}</p>
      ) : null}

      <div className="mb-4 rounded-xl border border-line bg-surface-alt px-5 py-5 sm:px-6 sm:py-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <span className="block text-[11px] font-semibold uppercase tracking-wide text-ink-400">
              CVs analysed
            </span>
            {loading ? (
              <Skeleton className="mt-3 h-12 w-24" />
            ) : (
              <span className="block mt-2 text-4xl sm:text-5xl font-bold leading-none text-ink-900 tabular-nums">
                {analysedCount}
              </span>
            )}
            <span className="block mt-3 text-sm text-ink-400">
              Every Analyse click (including cache hits)
            </span>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={loading || exporting}
              loading={exporting}
              onClick={async () => {
                setExporting(true);
                try {
                  await downloadRunsExportCsv();
                  toast.show({
                    tone: "success",
                    title: "Export complete",
                    message: "CV Analyser audit CSV downloaded.",
                  });
                } catch (e) {
                  toast.show({
                    tone: "danger",
                    title: "Export failed",
                    message: String(e.message || e) || "Could not export logs.",
                  });
                } finally {
                  setExporting(false);
                }
              }}
            >
              Export CSV
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={loading || loadingMore}
              onClick={() => load({ append: false, offset: 0 })}
            >
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="md:hidden space-y-3">
        {loading ? (
          <p className="text-sm text-ink-400 py-4">Loading logs…</p>
        ) : runs.length === 0 ? (
          <p className="text-sm text-ink-400 py-4">
            No analyses logged yet. Run Analyse on a CV to see history here.
          </p>
        ) : (
          runs.map((run) => {
            const { date, time } = splitDateTime(run.occurredAt);
            return (
              <div
                key={run.id}
                className="rounded-xl border border-line bg-white p-3 text-sm space-y-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-semibold text-ink-900">{date}</p>
                    <p className="text-xs text-ink-500">{time}</p>
                  </div>
                  <span
                    className={
                      run.status === "ok"
                        ? "text-green-700 text-xs font-semibold"
                        : "text-red-600 text-xs font-semibold"
                    }
                    title={run.errorMessage || undefined}
                  >
                    {run.status === "ok" ? "ok" : "error"}
                  </span>
                </div>
                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                  <dt className="text-ink-400">HR</dt>
                  <dd className="text-ink-700">{run.hrName || run.hrId || "—"}</dd>
                  <dt className="text-ink-400">CV file</dt>
                  <dd className="text-ink-900 truncate" title={run.filename || ""}>
                    {run.filename || "—"}
                  </dd>
                  <dt className="text-ink-400">Candidate</dt>
                  <dd className="text-ink-900">{run.candidateName || "—"}</dd>
                  <dt className="text-ink-400">Job</dt>
                  <dd className="text-ink-700 truncate" title={run.jobTitle || ""}>
                    {run.jobTitle || "—"}
                  </dd>
                  <dt className="text-ink-400">Verdict</dt>
                  <dd className="text-ink-700">{run.verdict || "—"}</dd>
                  <dt className="text-ink-400">Score</dt>
                  <dd className="text-ink-700">
                    {run.overallFitScore != null ? run.overallFitScore : "—"}
                  </dd>
                  <dt className="text-ink-400">Cached</dt>
                  <dd className="text-ink-700">{run.cached ? "Yes" : "No"}</dd>
                </dl>
              </div>
            );
          })
        )}
      </div>

      <div className="hidden md:block overflow-x-auto rounded-xl border border-line">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-surface-alt text-[11px] uppercase tracking-wide text-ink-400">
            <tr>
              <th className="px-3 py-2 font-semibold">Date</th>
              <th className="px-3 py-2 font-semibold">Time</th>
              <th className="px-3 py-2 font-semibold">HR</th>
              <th className="px-3 py-2 font-semibold">CV file</th>
              <th className="px-3 py-2 font-semibold">Candidate</th>
              <th className="px-3 py-2 font-semibold">Job</th>
              <th className="px-3 py-2 font-semibold">Verdict</th>
              <th className="px-3 py-2 font-semibold">Score</th>
              <th className="px-3 py-2 font-semibold">Cached</th>
              <th className="px-3 py-2 font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-ink-400">
                  Loading logs…
                </td>
              </tr>
            ) : runs.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-ink-400">
                  No analyses logged yet. Run Analyse on a CV to see history here.
                </td>
              </tr>
            ) : (
              runs.map((run) => {
                const { date, time } = splitDateTime(run.occurredAt);
                return (
                  <tr key={run.id} className="border-t border-line">
                    <td className="px-3 py-2 whitespace-nowrap text-ink-900">
                      {date}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-ink-700">
                      {time}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-ink-700">
                      {run.hrName || run.hrId || "—"}
                    </td>
                    <td
                      className="px-3 py-2 max-w-[10rem] truncate text-ink-900"
                      title={run.filename || ""}
                    >
                      {run.filename || "—"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-ink-900">
                      {run.candidateName || "—"}
                    </td>
                    <td
                      className="px-3 py-2 max-w-[8rem] truncate text-ink-700"
                      title={run.jobTitle || ""}
                    >
                      {run.jobTitle || "—"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-ink-700">
                      {run.verdict || (run.errorMessage ? "—" : "—")}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-ink-700">
                      {run.overallFitScore != null ? run.overallFitScore : "—"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-ink-700">
                      {run.cached ? "Yes" : "No"}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span
                        className={
                          run.status === "ok"
                            ? "text-green-700"
                            : "text-red-600"
                        }
                        title={run.errorMessage || undefined}
                      >
                        {run.status === "ok" ? "ok" : "error"}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && runs.length < total ? (
        <div className="mt-3">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={loadingMore}
            loading={loadingMore}
            onClick={() => load({ append: true, offset: runs.length })}
          >
            Load more ({runs.length} of {total})
          </Button>
        </div>
      ) : null}

      {!loading && total > 0 ? (
        <p className="mt-3 text-xs text-ink-400">
          Showing {runs.length} of {total} runs
        </p>
      ) : null}
    </Modal>
  );
}
